import { z } from 'zod';
import { paginationQuerySchema } from '../pagination.js';
import { COHERENCE_STATUTS, } from '../domain/copacking-coherence.js';
export const FAMILY_STATUSES = ['PROPOSEE', 'VALIDEE', 'REJETEE', 'DESACTIVEE'];
export const familyStatusSchema = z.enum(FAMILY_STATUSES);
export const FAMILY_STATUS_LABELS = {
    PROPOSEE: 'Proposée',
    VALIDEE: 'Validée',
    REJETEE: 'Rejetée',
    DESACTIVEE: 'Désactivée',
};
export const FAMILY_ORIGINS = ['MANUELLE', 'FAB_1_1', 'COPACKING_TABLE'];
export const familyOriginSchema = z.enum(FAMILY_ORIGINS);
export const FAMILY_ORIGIN_LABELS = {
    MANUELLE: 'Saisie manuelle',
    FAB_1_1: 'Détection FAB 1:1',
    COPACKING_TABLE: 'Grappe copacking (tables)',
};
const codproSchema = z
    .string({ required_error: 'Code article requis' })
    .trim()
    .min(1, 'Code article requis')
    .max(40, 'Le code article ne doit pas dépasser 40 caractères');
/**
 * Creation manuelle d'une famille. Une famille a toujours au moins deux membres :
 * une famille a un seul code n'a aucun sens fonctionnel.
 */
export const createFamilySchema = z.object({
    libelle: z.string().trim().max(255).nullable().default(null),
    codpros: z
        .array(codproSchema)
        .min(2, 'Une famille doit comporter au moins deux codes articles')
        .max(50, 'Une famille ne peut pas comporter plus de 50 codes')
        .refine((codes) => new Set(codes).size === codes.length, {
        message: 'Le même code article est présent plusieurs fois',
    }),
    /**
     * Statut a la creation manuelle. Par defaut VALIDEE : une saisie humaine
     * explicite vaut validation. Une proposition automatique arrive en PROPOSEE.
     */
    statut: z.enum(['PROPOSEE', 'VALIDEE']).default('VALIDEE'),
});
export const updateFamilySchema = z
    .object({
    libelle: z.string().trim().max(255).nullable().optional(),
    codpros: z
        .array(codproSchema)
        .min(2, 'Une famille doit comporter au moins deux codes articles')
        .max(50)
        .refine((codes) => new Set(codes).size === codes.length, {
        message: 'Le même code article est présent plusieurs fois',
    })
        .optional(),
})
    .refine((valeur) => Object.values(valeur).some((champ) => champ !== undefined), {
    message: 'Aucune modification fournie',
});
/** Validation ou rejet d'une proposition : decision humaine, tracee. */
export const decideFamilySchema = z.object({
    decision: z.enum(['VALIDER', 'REJETER']),
    commentaire: z.string().trim().max(2000).optional(),
});
/** Validation ou rejet d'une extension : décision humaine, tracée. */
export const decideFamilyExtensionSchema = z.object({
    decision: z.enum(['VALIDER', 'REJETER']),
    commentaire: z.string().trim().max(2000).optional(),
});
export const listFamilyExtensionsQuerySchema = z.object({
    statut: familyStatusSchema.optional(),
});
export const listFamiliesQuerySchema = paginationQuerySchema.extend({
    statut: familyStatusSchema.optional(),
    origine: familyOriginSchema.optional(),
    /** Recherche sur un code article membre ou sur le libelle de la famille. */
    search: z.string().trim().max(120).optional(),
});
/**
 * Seuil au-delà duquel une proposition est signalée comme large.
 *
 * Ce n'est **pas un plafond** : une famille de neuf codes peut être juste. Le
 * seuil sert seulement à attirer l'œil de la personne qui valide.
 */
export const FAMILLE_LARGE_SEUIL = 5;
export const coherenceQuerySchema = z.object({
    statuts: z
        .union([z.string(), z.array(z.enum(COHERENCE_STATUTS))])
        .transform((valeur) => (Array.isArray(valeur) ? valeur : valeur.split(','))
        .map((statut) => statut.trim())
        .filter((statut) => COHERENCE_STATUTS.includes(statut)))
        /** Par défaut, l'écran montre ce qui mérite un regard, pas le tout-venant. */
        .default(['NON_DECLARE']),
    search: z.string().trim().max(120).optional(),
});
//# sourceMappingURL=family.js.map